'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';

import { Grid } from '@/components/cms/Grid';
import { StoryCard } from '@/components/cms/StoryCard';
import { Button } from '@/components/ui/Button/Button';
import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';

import { NewsPage } from '@/lib/contentful/news';
import {
  ANIMATION_STAGGER_MS,
  DESKTOP_VISIBLE_COUNT,
  MOBILE_VISIBLE_COUNT,
} from './StoriesList.helpers';
import styles from './StoriesList.module.scss';
import type { StoriesListProps } from './StoriesList.types';
import TagsFilter from './components/TagsFilter';

export const StoriesList = ({
  stories,
  title,
  filters,
  latest,
  buttonLabel,
  buttonUrl,
}: StoriesListProps) => {
  const t = useTranslations('common');
  const [collapsed, setCollapsed] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);
  const [filteredStories, setFilteredStories] = useState<NewsPage[]>(stories);

  const hasMoreMobile = !latest && filteredStories.length > MOBILE_VISIBLE_COUNT;
  const hasMoreDesktop = filteredStories.length > DESKTOP_VISIBLE_COUNT;
  const mobileOnly = hasMoreMobile && !hasMoreDesktop;

  const handleExpand = useCallback(() => {
    setCollapsed(false);
    requestAnimationFrame(() => {
      setExpanded(true);
    });
  }, []);

  const sectionRef = useRef<HTMLElement>(null);

  const handleTagClick = (tag: string) => {
    setExpanded(false);
    if (selectedTag === tag) {
      setSelectedTag(undefined);
      setFilteredStories(stories);
    } else {
      setSelectedTag(tag);
      const filtered = stories.filter((item) =>
        item?.tagsCollection?.items.some((t) => t?.value === tag)
      );
      setFilteredStories(filtered);
    }
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section ref={sectionRef} className={clsx(styles.root, latest && styles.latestStories)}>
      {filters && (
        <TagsFilter
          newsPagesCollection={stories || []}
          onTagClick={handleTagClick}
          selectedTag={selectedTag}
        />
      )}

      {title && <h2 className={styles.title}>{title}</h2>}

      <Grid columns={4} columnsMobile={1} gap="xs" padding="none" slider={!!latest}>
        {filteredStories.map((story, index) => {
          const isHiddenMobileOnly = index >= MOBILE_VISIBLE_COUNT && index < DESKTOP_VISIBLE_COUNT;
          const isHiddenAll = index >= DESKTOP_VISIBLE_COUNT;
          const needsReveal = isHiddenMobileOnly || isHiddenAll;
          const delay = Math.max(0, index - MOBILE_VISIBLE_COUNT) * ANIMATION_STAGGER_MS;

          return (
            <div
              key={index}
              className={clsx(
                styles.item,
                !latest && collapsed && isHiddenMobileOnly && styles.hiddenMobileOnly,
                collapsed && isHiddenAll && styles.hiddenAll,
                !collapsed && needsReveal && styles.revealReady,
                expanded && needsReveal && styles.revealed
              )}
              style={needsReveal ? ({ '--delay': `${delay}ms` } as CSSProperties) : undefined}
            >
              <StoryCard {...story} />
            </div>
          );
        })}
      </Grid>

      {buttonUrl ? (
        <div className={styles.seeMoreWrapper}>
          <LinkButton href={buttonUrl} variant="outline" iconAfter={<Icon name="plus" size={16} />}>
            {buttonLabel ?? t('seeMoreStories')}
          </LinkButton>
        </div>
      ) : (
        hasMoreMobile &&
        collapsed && (
          <div className={clsx(styles.seeMoreWrapper, mobileOnly && styles.seeMoreMobileOnly)}>
            <Button
              variant="outline"
              iconAfter={<Icon name="plus" size={16} />}
              onClick={handleExpand}
            >
              {buttonLabel ?? t('seeMoreStories')}
            </Button>
          </div>
        )
      )}
    </section>
  );
};
